/**
 * SlotActions Component
 *
 * Unified container component that renders both lifecycle and record action buttons
 * in fixed-width slots with consistent vertical alignment. Actions are sorted by
 * their `order` field and non-applicable actions render as empty placeholders.
 *
 * This solves the problem of action buttons shifting horizontally across rows
 * because only applicable actions were rendered. By using fixed slots, buttons
 * maintain consistent positions for easier vertical scanning.
 *
 * Order conventions:
 * - Lifecycle actions: 10-90 (activate=10, expire=20, duplicate=30, etc.)
 * - Record actions: 100+ (delete=100, undelete=110, purge=120)
 */

import { Space } from 'antd'
import { LifecycleActionButton } from './LifecycleActionButton'
import { RecordActionButton, RecordActionKey } from './RecordActionButton'
import type {
  LifecycleMetadata,
  LifecycleAction,
  RecordActionsMetadata,
  RecordAction,
  FieldMetadata,
} from '@/services/metadataService'

/** Width of each action slot in pixels (matches small button width) */
const SLOT_WIDTH = 32

/**
 * Internal type for a unified action item
 */
interface ActionItem {
  /** Unique key for this action */
  key: string
  /** Display order (lower = first) */
  order: number
  /** Type of action for routing to correct button component */
  type: 'lifecycle' | 'record'
  /** Action configuration */
  config: LifecycleAction | RecordAction
}

/**
 * Props for SlotActions container
 */
export interface SlotActionsProps {
  /** Lifecycle metadata from backend (optional - may not exist for non-lifecycle tables) */
  lifecycle?: LifecycleMetadata
  /** Record actions metadata from backend */
  recordActions?: RecordActionsMetadata
  /** Field metadata for diff display in splicing preview (optional) */
  fieldMetadata?: Record<string, FieldMetadata>
  /** Key fields from comparison config (for diff display in splicing preview) */
  comparisonKeyFields?: string[]
  /** Field to use for timeline segment labels (from comparison.timeline_label_field), or null to disable labels */
  timelineLabelField?: string | null
  /** Whether to prefix "NEW" to the focus segment in timeline (from comparison.show_new_prefix) */
  showNewPrefix?: boolean
  /** Fields to use for friendly record titles (from display.title_fields) */
  titleFields?: string[]
  /** The record to show actions for */
  record: Record<string, unknown>
  /** API endpoint (e.g., '/offerings') */
  endpoint: string
  /** Entity label for messages (e.g., 'Offering') */
  entityLabel: string
  /** Called after successful action */
  onSuccess?: () => void
  /** Called for lifecycle actions that open an editor (e.g., duplicate) */
  onOpenEditor?: (record: Record<string, unknown>, actionKey: string) => void
}

/**
 * Check if a record is soft-deleted
 */
function isRecordDeleted(record: Record<string, unknown>): boolean {
  return record.deletedAt !== null && record.deletedAt !== undefined
}

/**
 * Check if an action is applicable for the current record state
 */
function isActionApplicable(
  action: ActionItem,
  currentState: string | undefined,
  isDeleted: boolean
): boolean {
  if (action.type === 'lifecycle') {
    // Deleted records cannot participate in lifecycle transitions
    if (isDeleted) return false

    // Lifecycle actions check fromStates
    const lifecycleConfig = action.config as LifecycleAction
    return lifecycleConfig.fromStates?.includes(currentState ?? '') ?? false
  } else {
    // Record actions check requiresDeleted
    const recordConfig = action.config as RecordAction

    // requiresDeleted=true means only show for deleted records
    // requiresDeleted=false/undefined means only show for non-deleted records
    if (recordConfig.requiresDeleted) {
      if (!isDeleted) return false
    } else {
      if (isDeleted) return false
    }

    // Check fromStates if specified (lifecycle-aware filtering)
    if (recordConfig.fromStates && recordConfig.fromStates.length > 0 && currentState) {
      if (!recordConfig.fromStates.includes(currentState)) {
        return false
      }
    }

    return true
  }
}

/**
 * SlotActions Component
 *
 * Renders a fixed-width slot for each possible action position. Actions that
 * are not applicable for the current record render as invisible placeholders,
 * maintaining consistent vertical alignment across rows.
 *
 * @example Lifecycle table (offerings)
 * ```tsx
 * <SlotActions
 *   lifecycle={metadata.lifecycle}
 *   recordActions={metadata.record_actions}
 *   record={offering}
 *   endpoint="/offerings"
 *   entityLabel="Offering"
 *   onSuccess={() => refetch()}
 *   onOpenEditor={(record, action) => openDuplicateDrawer(record)}
 * />
 * ```
 *
 * @example Non-lifecycle table (customers)
 * ```tsx
 * <SlotActions
 *   recordActions={metadata.record_actions}
 *   record={customer}
 *   endpoint="/customers"
 *   entityLabel="Customer"
 *   onSuccess={() => refetch()}
 * />
 * ```
 */
export function SlotActions({
  lifecycle,
  recordActions,
  fieldMetadata,
  comparisonKeyFields,
  timelineLabelField,
  showNewPrefix,
  titleFields,
  record,
  endpoint,
  entityLabel,
  onSuccess,
  onOpenEditor,
}: SlotActionsProps) {
  const isDeleted = isRecordDeleted(record)

  // Get current lifecycle state (if applicable)
  const currentState = lifecycle?.enabled
    ? (record[lifecycle.stateField] as string | undefined)
    : undefined

  // Step 1: Collect all actions with their order
  const allActions: ActionItem[] = []

  // Add lifecycle actions (only for non-deleted records in lifecycle-enabled tables)
  if (lifecycle?.enabled && lifecycle.actions && !isDeleted) {
    Object.entries(lifecycle.actions).forEach(([key, config]) => {
      allActions.push({
        key,
        order: config.order ?? 50, // Default to middle if not specified
        type: 'lifecycle',
        config,
      })
    })
  }

  // Add record actions (filtered by current deleted state context)
  // This determines which SLOTS exist - normal view vs trash view
  if (recordActions) {
    Object.entries(recordActions).forEach(([key, config]) => {
      if (!config) return

      // Only include actions that match the current deleted state context
      // This ensures normal view shows delete slot, trash view shows undelete/purge slots
      const matchesContext = config.requiresDeleted === isDeleted
      if (matchesContext) {
        allActions.push({
          key,
          order: config.order ?? 100, // Default to after lifecycle actions
          type: 'record',
          config,
        })
      }
    })
  }

  // Step 2: Sort by order
  allActions.sort((a, b) => a.order - b.order)

  // No actions at all - don't render empty container
  if (allActions.length === 0) {
    return null
  }

  // Step 3: Render slots
  // Stop propagation to prevent row click from firing when clicking action buttons
  return (
    <Space size={4} wrap={false} onClick={(e) => e.stopPropagation()}>
      {allActions.map((action) => {
        const isApplicable = isActionApplicable(action, currentState, isDeleted)

        if (!isApplicable) {
          // Render empty placeholder slot to maintain alignment
          return (
            <span
              key={action.key}
              style={{
                display: 'inline-block',
                width: SLOT_WIDTH,
                height: 24, // Match small button height
              }}
            />
          )
        }

        // Render actual button based on action type
        if (action.type === 'lifecycle') {
          return (
            <LifecycleActionButton
              key={action.key}
              actionKey={action.key}
              action={action.config as LifecycleAction}
              record={record}
              endpoint={endpoint}
              entityLabel={entityLabel}
              fieldMetadata={fieldMetadata}
              comparisonKeyFields={comparisonKeyFields}
              timelineLabelField={timelineLabelField}
              showNewPrefix={showNewPrefix}
              titleFields={titleFields}
              onSuccess={onSuccess}
              onOpenEditor={onOpenEditor}
            />
          )
        } else {
          return (
            <RecordActionButton
              key={action.key}
              actionKey={action.key as RecordActionKey}
              action={action.config as RecordAction}
              record={record}
              endpoint={endpoint}
              entityLabel={entityLabel}
              onSuccess={onSuccess}
            />
          )
        }
      })}
    </Space>
  )
}

/**
 * Calculate the width needed for the actions column
 *
 * This calculates the maximum number of slots needed across both normal
 * and trash views to ensure the column width is consistent.
 *
 * @param lifecycle Lifecycle metadata (optional)
 * @param recordActions Record actions metadata (optional)
 * @returns Width in pixels for the actions column
 */
export function calculateSlotActionsWidth(
  lifecycle?: LifecycleMetadata,
  recordActions?: RecordActionsMetadata
): number {
  const PADDING = 16

  // Count lifecycle actions (normal view only)
  const lifecycleCount =
    lifecycle?.enabled && lifecycle.actions ? Object.keys(lifecycle.actions).length : 0

  // Count record actions by context
  let normalRecordCount = 0
  let trashRecordCount = 0

  if (recordActions) {
    Object.values(recordActions).forEach((action) => {
      if (!action) return
      if (action.requiresDeleted) {
        trashRecordCount++
      } else {
        normalRecordCount++
      }
    })
  }

  // Normal view: lifecycle + normal record actions
  const normalViewSlots = lifecycleCount + normalRecordCount
  // Trash view: trash record actions only (lifecycle hidden for deleted)
  const trashViewSlots = trashRecordCount

  // Use maximum of both views to ensure consistent column width
  const maxSlots = Math.max(normalViewSlots, trashViewSlots, 1)

  return maxSlots * SLOT_WIDTH + PADDING + (maxSlots - 1) * 4 // 4px gap between slots
}
